import { Link } from "react-router-dom";
import SEO from "../components/SEO.jsx";
import { BRAND } from "@blackthorn/shared";

/** Shared layout for legal pages (Terms of Service & Privacy Policy). */
function LegalPage({ children, title, description, path, updated }: { children: React.ReactNode; title: string; description: string; path: string; updated: string }) {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO title={`${title} - ${BRAND.name}`} description={description} path={path} keywords={`${title.toLowerCase()}, ${BRAND.name.toLowerCase()} legal`} />
      <header className="border-b border-bt-border bg-bt-surface">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/blackthorn-icon.svg" alt="" className="w-7 h-7" />
            <span className="font-semibold">{BRAND.name}</span>
          </Link>
          <Link to="/docs" className="text-bt-muted text-sm hover:text-bt-text ml-auto">Docs</Link>
        </div>
      </header>
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">{title}</h1>
        <p className="text-bt-muted text-sm mb-10">Last updated: {updated}</p>
        <div className="space-y-8 text-sm leading-relaxed text-bt-text/90">
          {children}
          <p className="text-xs text-bt-muted">
            Questions about this document? Contact the {BRAND.name} team via the support server linked from the{" "}
            <Link to="/" className="underline hover:text-bt-text">homepage</Link>.
          </p>
        </div>
      </main>
      <footer className="border-t border-bt-border py-6 text-center text-xs text-bt-muted">
        {BRAND.name} - {BRAND.tagline}
      </footer>
    </div>
  );
}

export function TermsOfService(): React.ReactElement {
  return (
    <LegalPage
      title="Terms of Service"
      path="/terms"
      updated="October 13, 2026"
      description="Terms of Service for Blackthorn: rules for using the Blackthorn Discord bot, dashboard, and verification service."
    >
      <section>
        <h2 className="text-lg font-semibold mb-2">1. Agreement</h2>
        <p>
          These Terms of Service ("Terms") govern your use of the {BRAND.name} Discord bot, web dashboard,
          verification service, and related websites (collectively, the "Service"). By inviting the bot to a
          Discord server, using the dashboard, or completing verification, you agree to these Terms. If you use
          the Service on behalf of a server community, you confirm you have authority to bind that community.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">2. Eligibility</h2>
        <p>
          You must comply with Discord's Terms of Service and Community Guidelines to use the Service.
          You may not use the Service if your Discord account has been disabled or terminated for violations
          of those terms. Verification requires a Discord account you own.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">3. The Service</h2>
        <p>{BRAND.name} provides:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Identity verification</strong> - proving control of a member's Discord account through a web flow: Discord sign-in, security screening, and CAPTCHA.</li>
          <li><strong>Access control</strong> - verified roles, unverified roles, and whitelist management.</li>
          <li><strong>Security screening</strong> - alt/ban-evasion risk scoring backed by logged (hashed) verification IPs, and ban checks at the verification door. VPN/proxy blocking is coming soon.</li>
          <li><strong>Security automation</strong> - anti-raid, anti-bot, anti-alt, and anti-spam detection with configurable actions, all defaulting to dry-run (log-only) mode.</li>
          <li><strong>Moderation tooling</strong> - warnings, cases, timeouts, kicks, bans, purges, and channel controls, with audit logging.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">4. Acceptable use</h2>
        <p>You agree <strong>not</strong> to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Verify an account you do not control, or transfer a verified link to another person.</li>
          <li>Attempt to bypass, automate, or defeat verification, the CAPTCHA, rate limits, or other Service controls.</li>
          <li>Use the Service to harass, doxx, spam, raid, or otherwise harm individuals or communities.</li>
          <li>Exploit vulnerabilities, scrape the Service, or resell access without written permission.</li>
          <li>Use the Service for servers whose primary purpose violates Discord's policies.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">5. Server administrator responsibilities</h2>
        <p>
          Server administrators configure the Service's actions (roles, thresholds, quarantine, moderation) and
          are responsible for how those actions are applied in their communities. {BRAND.name} provides the
          tooling; moderation outcomes and community rules remain the administrator's responsibility. Automated
          security actions are assistive and may produce false positives; administrators should review incidents
          in dry-run mode before enabling live actions.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">6. Verification tokens &amp; links</h2>
        <p>
          Verification links are single-use and expire quickly (10 minutes by default). Never share a
          verification link - whoever completes it links <em>their</em> account to <em>your</em> Discord ID.
          {BRAND.name} never asks for your Discord password. If someone asks for that in our name, it is not us.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">7. Availability &amp; changes</h2>
        <p>
          The Service is provided as-is and may be modified, suspended, or discontinued (in whole or for a
          server) at any time, including for maintenance, abuse, or upstream changes to Discord or
          Cloudflare. Features that depend on third-party APIs may change when those providers change. We aim
          to communicate material changes in the support server and docs.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">8. Suspension &amp; termination</h2>
        <p>
          We may suspend or terminate access for violation of these Terms, abuse of the Service, or where
          required by law or third-party provider policy. Server administrators may remove the bot and request
          data deletion at any time (see the <Link to="/privacy">Privacy Policy</Link>).
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">9. Disclaimers &amp; limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, the Service is provided "as is" and "as available" without
          warranties of any kind. {BRAND.name} is not liable for indirect, incidental, or consequential damages,
          lost data, lost profits, or moderation disputes arising from use of the Service, including actions
          taken by automated security features or by server administrators using the tooling.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">10. Changes to these Terms</h2>
        <p>
          We may update these Terms; material changes will be reflected by the "Last updated" date above and,
          where practical, announced in the support server. Continued use after changes take effect constitutes
          acceptance.
        </p>
      </section>
    </LegalPage>
  );
}

export function PrivacyPolicy(): React.ReactElement {
  return (
    <LegalPage
      title="Privacy Policy"
      path="/privacy"
      updated="October 13, 2026"
      description="Privacy Policy for Blackthorn: what data the bot stores, who can see it, retention, and how to have your data removed."
    >
      <section>
        <h2 className="text-lg font-semibold mb-2">1. Overview</h2>
        <p>
          {BRAND.name} is a Discord security and verification service. This policy explains what data we collect,
          why, who can see it, how long we keep it, and how to have it removed. The short version: we store the
          minimum needed to run verification and security, we never show raw IP addresses to server staff, and
          you can have your data deleted.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">2. Data we collect</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Discord identifiers</strong> - your Discord user ID and (for dashboard sessions) your Discord username and avatar URL, obtained via Discord OAuth with the <code>identify</code> and <code>guilds</code> scopes only.</li>
          <li><strong>Verification screening signals</strong> - the verification timestamp and method, plus risk signals: an alt risk score (0-100), the salted one-way IP hash (for alt correlation; the raw IP is never stored and the hash cannot be reversed or compared across servers), and whether the account matched a server blocklist.</li>
          <li><strong>Verification state</strong> - pending / verified / rejected / expired / revoked, plus expiry timestamps when configured.</li>
          <li><strong>Whitelist records</strong> - your user ID, provider identity, approval status, approving staff member, and any reason given.</li>
          <li><strong>Security &amp; moderation records</strong> - incidents (e.g. raid/spam events), moderation cases (warnings, bans, timeouts) involving you, and audit-log entries for actions taken by or on you.</li>
          <li><strong>Server configuration</strong> - per-guild settings like roles, thresholds, and policy choices (not personal data about you).</li>
          <li><strong>CAPTCHA result</strong> - the CAPTCHA pass/fail for a verification attempt. The token itself is never stored, and CAPTCHA data is processed by Cloudflare under <a href="https://www.cloudflare.com/privacypolicy/" className="underline" target="_blank" rel="noreferrer">Cloudflare's privacy policy</a>.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">3. What we never collect or store</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Passwords</strong> - we never see your Discord password.</li>
          <li><strong>Raw IP addresses</strong> - never stored, never displayed to staff. Only a salted one-way hash is retained for abuse prevention.</li>
          <li><strong>Message content at rest</strong> - anti-spam evaluates messages transiently to score spam; content is not persisted beyond the incident record's summary and metadata.</li>
          <li><strong>Verification tokens or secrets</strong> - never written to logs or audit records.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">4. Why we process it (legal bases)</h2>
        <p>
          We process this data to provide the service the server owner requested: verifying identity (contract),
          keeping communities safe (legitimate interest in security and abuse prevention), keeping records of
          moderation actions (legitimate interest / legal obligation of the community), and operating the
          dashboard securely (legitimate interest). Where consent is required, it is obtained at the point of
          verification.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">5. Who can see your data</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Server staff</strong> - verification status, whitelist entries, incidents, and audit events for <em>their</em> server only. Access requires Manage Server or an explicit grant and is checked server-side on every request.</li>
          <li><strong>{BRAND.name} operators</strong> - only for maintenance, abuse investigation, or support, under the same per-server restrictions.</li>
          <li><strong>Processors we rely on</strong> - Supabase (database hosting), Cloudflare (Workers, Pages, Turnstile CAPTCHA), Discord (bot/identity APIs). Each processes data under its own terms.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">6. Retention</h2>
        <p>
          Verification and identity data is kept while your link is active and per the server's configured
          expiry/revocation rules. Audit logs follow the per-guild <code>audit_retention_days</code> setting
          (default 90 days). Sessions expire after 12 hours and are revoked on logout. Expired or deleted
          records are removed or anonymized on schedule.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">7. Your rights &amp; data removal</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Unlink yourself</strong> - ask staff to remove your identity link at any time.</li>
          <li><strong>Staff unlink</strong> - server staff can remove your identity and whitelist entries from the dashboard.</li>
          <li><strong>Access / copy</strong> - ask the staff of the server you verified in; they can see your verification and audit records for that server.</li>
          <li><strong>Erasure</strong> - contact the staff of the server you verified in to request deletion; they can escalate to the {BRAND.name} team for full removal.</li>
          <li><strong>Objection / complaint</strong> - contact us via the support server; you may also complain to your local data-protection authority if you believe processing is unlawful.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">8. Children</h2>
        <p>
          The Service is not directed at children under 13 (or under 16 in the EEA/UK, where applicable), and we
          do not knowingly process their data. Server administrators are responsible for their communities'
          compliance with platform age rules. If you believe a child's data was processed, contact us and we
          will delete it.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">9. Security</h2>
        <p>
          Dashboard sessions are HMAC-signed HttpOnly cookies (Secure in production) with server-side revocation.
          Database access uses row-level security (deny by default) and service-role credentials that never leave
          server-side configuration. Secrets are rotated per the operational runbook, and audit logs never
          contain tokens or secrets.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">10. Changes</h2>
        <p>
          Material changes to this policy will be reflected in the "Last updated" date and, where practical,
          announced in the support server.
        </p>
      </section>
    </LegalPage>
  );
}
